'use client';

import { Icon } from '@/general/Icons/Icon';
import { clsx } from 'clsx';
import { ComponentProps, FC, ReactNode } from 'react';

import { ProductListDataRow } from '@/types/CataloguePage.types';

import { Button } from '../Button/Button';
import { useToggleWatchList } from './hooks/useToggleWatchList';

const addToWatchListButtonVariants = {
  default: '',
  comparisonHeader:
    'w-full rounded-none bg-bac-tie1 sm:w-auto sm:rounded-xs sm:border! sm:border-bor-neu-mut! [--shade-gradient-col1:var(--sta-ter-hov)] [--shade-gradient-col2:var(--sta-ter-def)]',
} as const;

type AddToWatchListButtonVariant = keyof typeof addToWatchListButtonVariants;

type LabelsOverwrite = {
  save: ReactNode;
  saved: ReactNode;
};

type AddToWatchListButtonProps = {
  sku: string;
  className?: string;
  variant?: AddToWatchListButtonVariant;
  labelsOverwrite?: LabelsOverwrite;
  disableSnackbar?: boolean;
  product?: Partial<ProductListDataRow>;
  onWatchlistStatusChange?: () => Promise<void>;
} & {
  buttonProps?: Pick<ComponentProps<typeof Button>, 'variant' | 'fullWidth' | 'size'>;
};

export const AddToWatchListButton: FC<AddToWatchListButtonProps> = ({
  sku,
  className,
  variant: addToWatchListVariant = 'default',
  labelsOverwrite = {
    save: 'Save',
    saved: 'Saved',
  },
  product = {},
  onWatchlistStatusChange,
  disableSnackbar = false,
  ...props
}) => {
  const { userAddedItem, handleToggle, isLoading } = useToggleWatchList(
    sku,
    disableSnackbar,
    onWatchlistStatusChange,
    product,
  );

  const buttonLabel = userAddedItem ? labelsOverwrite.saved : labelsOverwrite.save;

  return (
    <Button
      size="small"
      {...props.buttonProps}
      onClick={handleToggle}
      data-testid={`AddToWatchList-component-button-${userAddedItem ? 'Saved' : 'Save'}`}
      startIcon={
        userAddedItem ? (
          <span className="text-pin-500">
            <Icon name="heart-filled" />
          </span>
        ) : (
          <Icon name="heart" />
        )
      }
      className={clsx('watchlistButton', addToWatchListButtonVariants[addToWatchListVariant], className)}
      disabled={isLoading}
    >
      {buttonLabel}
    </Button>
  );
};
